import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Input } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import ImageUploader from '../../components/ImageUploader';
import VideoUploader from '../../components/VideoUploader';
import { useAuth } from '../../context/AuthContext';
import { kindsOf, sidesOf, unitsFor, vocabOf } from '../../utils/vertical';
import CategoryFields from '../../components/CategoryFields';
import SearchableSelect from '../../components/SearchableSelect';
import SpecFields from '../../components/SpecFields';
import { useToast } from '../../context/ToastContext';

const UNITS = ['1 pc', '250 g', '500 g', '1 kg', '5 kg', '1 litre', '1 packet', '1 dozen'];
const PRICE_UNITS = ['per service', 'per hour', 'per day', 'per sqft', 'per visit'];

/**
 * Shared add form for products and services — the `kind` query parameter
 * selects which. Categories are limited to the ones the partner registered for.
 */
const AddCatalogueItemPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { state } = useLocation();
  const { partner } = useAuth();
  const toast = useToast();

  const kind = params.get('kind') === 'service' ? 'service' : 'product';
  const isProduct = kind === 'product';
  const editing = (state && state.item) || null;
  // A restaurant's product is a dish: veg mark, menu section, sizes and extras.
  const vocab = vocabOf(partner);
  // A hotel with a restaurant still adds dishes with veg / non-veg and add-ons.
  const food = isProduct && sidesOf(partner).menu;
  const customFields = vocab.customFields || kindsOf(partner).some((k) => ['shop', 'showroom', 'garage'].includes(k));
  // Units in the trade's own words: plates for a dhaba, per trip for a tour operator.
  const sideUnits = unitsFor(partner, isProduct ? 'product' : 'service');
  const noun = food ? 'item' : (isProduct ? 'product' : 'service');

  const [categories, setCategories] = useState([]);
  /*
   * What the chosen category asks for, and the units it sells by - both set
   * by admin, so a hospital is asked about appointments while a kiryana shop
   * is asked nothing beyond its price and unit.
   */
  const [fieldDefs, setFieldDefs] = useState([]);
  const [attrs, setAttrs] = useState(() => Object.fromEntries(
    (((editing && editing.attributes) || [])).map((a) => [a.key, a.values.length > 1 ? a.values : (a.values[0] || '')]),
  ));
  const [attrToggles, setAttrToggles] = useState({});
  const [showCategory, setShowCategory] = useState(false);
  const [catUnits, setCatUnits] = useState([]);
  // The shop's own categories - what an item is filed under.
  const [shelves, setShelves] = useState([]);
  // Admin's units for this category win; the trade's defaults are the fallback.
  const unitChoices = catUnits.length ? catUnits : (sideUnits.length ? sideUnits : (isProduct ? UNITS : PRICE_UNITS));
  /*
   * The shop's own categories, parents and sub-categories flattened into one
   * searchable list: "Dairy" and "Dairy › Milk" both pick a real shelf.
   */
  const kindNow = isProduct ? 'product' : 'service';
  const shelfRows = [];
  (shelves || []).forEach((top) => {
    if (top.forKind === 'both' || top.forKind === kindNow) shelfRows.push({ id: top._id, label: top.name });
    ((top.children) || []).forEach((kid) => {
      if (kid.forKind === 'both' || kid.forKind === kindNow) shelfRows.push({ id: kid._id, label: `${top.name} › ${kid.name}` });
    });
  });
  const sections = shelfRows.map((r) => r.label);

  /** A category typed here is created on the shop, ready for the next item. */
  const addSection = async (name) => {
    try {
      const made = (await api.partners.createCategory({ name, forKind: 'both' })).data;
      setShelves((list) => [...list, { ...made, children: [] }]);
      setForm((f) => ({ ...f, section: made.name, sectionId: made._id }));
    } catch (err) { /* the item still saves with the typed name */ }
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState((editing && editing.images) || []);
  const [video, setVideo] = useState((editing && editing.video) || '');
  const [form, setForm] = useState({
    title: (editing && editing.title) || '',
    description: (editing && editing.description) || '',
    price: editing ? String(editing.price) : '',
    mrp: editing && editing.mrp ? String(editing.mrp) : '',
    category: (editing && ((editing.category && editing.category._id) || editing.category)) || '',
    unit: (editing && editing.unit) || '1 pc',
    stock: editing && editing.stock !== undefined ? String(editing.stock) : '10',
    brand: (editing && editing.brand) || '',
    // Food: what the customer filters and customises by.
    foodType: (editing && editing.foodType) || (editing && editing.isVeg === false ? 'non_veg' : 'veg'),
    section: (editing && editing.menuCategory) || '',
    sectionId: (editing && ((editing.section && editing.section._id) || editing.section)) || null,
    variants: ((editing && editing.variants) || []).map((v) => ({ label: v.label, price: String(v.price) })),
    addonGroups: ((editing && editing.addonGroups) || []).map((g) => ({
      title: g.title,
      required: Boolean(g.required),
      multi: g.multi !== false,
      options: (g.options || []).map((o) => ({ name: o.name, price: String(o.price) })),
    })),
    // Showroom goods: the exact model, what it comes with and after-sales.
    modelNumber: (editing && editing.modelNumber) || '',
    warranty: (editing && editing.warranty) || '',
    specs: ((editing && editing.specs) || []).map((s) => ({ key: s.key, value: s.value })),
    installationAvailable: Boolean(editing && editing.installationAvailable),
    deliveryAvailable: Boolean(editing && editing.deliveryAvailable),
    priceUnit: (editing && editing.priceUnit) || 'per service',
    durationMinutes: editing && editing.durationMinutes ? String(editing.durationMinutes) : '60',
  });

  useEffect(() => {
    api.partners.myCategories().then((r) => setShelves(r.data || [])).catch(() => setShelves([]));
  }, []);

  // The category decides the questions and the units - reload both when it changes.
  useEffect(() => {
    if (!form.category) { setFieldDefs([]); setCatUnits([]); return; }
    const appliesTo = isProduct ? 'product' : 'service';
    api.categories.fields(form.category, { appliesTo })
      .then((r) => {
        const defs = r.data || [];
        setFieldDefs(defs);
        setAttrToggles((t) => {
          const next = { ...t };
          defs.filter((d) => d.optionalToggle).forEach((d) => {
            if (next[d.key] === undefined) next[d.key] = Boolean((((editing && editing.attributes) || [])).find((a) => a.key === d.key));
          });
          return next;
        });
      })
      .catch(() => setFieldDefs([]));
    api.categories.units(form.category, { appliesTo })
      .then((r) => setCatUnits(r.data || []))
      .catch(() => setCatUnits([]));
  }, [form.category, isProduct, editing]);

  useEffect(() => {
    // Prefer the partner's own categories; fall back to the full list.
    const mine = (partner && partner.categories) || [];
    // The business's main category is what an item is listed under unless the
    // partner says otherwise, so nothing has to be picked while adding.
    const primary = (partner && partner.primaryCategory && (partner.primaryCategory._id || partner.primaryCategory)) || null;
    if (mine.length && typeof mine[0] === 'object') {
      setCategories(mine);
      setForm((f) => ({ ...f, category: f.category || ((primary && mine.find((c) => String(c._id) === String(primary))) ? primary : mine[0]._id) }));
      return;
    }
    api.categories.list({ limit: 100 }).then((res) => {
      const list = res.data || [];
      const ids = mine.map(String);
      const filtered = ids.length ? list.filter((c) => ids.includes(String(c._id))) : list;
      const final = filtered.length ? filtered : list;
      setCategories(final);
      const fallback = (primary && final.find((c) => String(c._id) === String(primary))) ? primary : (final[0] && final[0]._id);
      if (fallback) setForm((f) => ({ ...f, category: f.category || fallback }));
    }).catch(() => setCategories([]));
  }, [partner]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) { setError('Enter a name'); return; }
    if (!form.price) { setError('Enter a price'); return; }
    if (!form.category) { setError('Choose a category'); return; }

    setSaving(true);
    try {
      const base = {
        title: form.title.trim(),
        description: form.description,
        price: Number(form.price),
        mrp: Number(form.mrp) || Number(form.price),
        category: form.category,
        images,
        video,
      };
      // Only answer what is on screen: a switch that is off means "not asked".
      const answers = {};
      fieldDefs.forEach((f) => {
        if (f.optionalToggle && !attrToggles[f.key]) return;
        const v = attrs[f.key];
        if (v !== undefined && v !== '' && !(Array.isArray(v) && !v.length)) answers[f.key] = v;
      });
      const payload = isProduct
        ? {
          ...base,
          attributes: answers,
          section: form.sectionId || null,
          menuCategory: form.sectionId ? undefined : form.section.trim(),
          unit: form.unit,
          stock: Number(form.stock) || 0,
          brand: form.brand,
          ...(food ? {
            foodType: form.foodType,
            isVeg: form.foodType === 'veg',
            variants: form.variants.filter((v) => v.label.trim()).map((v) => ({ label: v.label.trim(), price: Number(v.price) || 0 })),
            addonGroups: form.addonGroups.filter((g) => g.title.trim()).map((g) => ({
              title: g.title.trim(),
              required: g.required,
              multi: g.multi,
              options: g.options.filter((o) => o.name.trim()).map((o) => ({ name: o.name.trim(), price: Number(o.price) || 0 })),
            })),
          } : {}),
          modelNumber: form.modelNumber,
          warranty: form.warranty,
          specs: form.specs.filter((s) => s.key.trim()),
          installationAvailable: form.installationAvailable,
          deliveryAvailable: form.deliveryAvailable,
        }
        : {
          ...base,
          attributes: answers,
          section: form.sectionId || null,
          menuCategory: form.sectionId ? undefined : form.section.trim(),
          specs: form.specs.filter((sp) => sp.key.trim()),
          priceUnit: form.priceUnit,
          durationMinutes: Number(form.durationMinutes) || 60,
        };

      const service = isProduct ? api.products : api.services;
      if (editing) await service.update(editing._id, payload);
      else await service.create(payload);

      toast.success(`Your ${kind} is live for customers.`, editing ? 'Updated' : 'Added');
      navigate(`/partner/catalogue?tab=${isProduct ? 'products' : 'services'}`, { replace: true });
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader
        title={`${editing ? 'Edit' : 'Add'} ${noun}`}
        subtitle={isProduct ? 'Something you sell' : 'Something customers can book'}
      />

      <Reveal className="card sh-sm">
        <form onSubmit={submit}>
          <ImageUploader
            label={isProduct ? 'Product photos' : 'Service photos'}
            folder={isProduct ? 'product' : 'service'}
            value={images}
            onChange={setImages}
            max={6}
          />
          <VideoUploader
            label={isProduct ? 'Product video (optional)' : 'Service video (optional)'}
            hint={isProduct ? 'Unboxing, close-ups or the product in use.' : 'Show the work being done, before and after.'}
            folder={isProduct ? 'product' : 'service'}
            value={video}
            onChange={setVideo}
          />

          <Input
            label={isProduct ? 'Product name' : 'Service name'}
            icon={isProduct ? '📦' : '🛠️'}
            placeholder={isProduct ? 'e.g. Basmati Rice' : 'e.g. AC service & gas top-up'}
            value={form.title}
            onChange={(e) => { setForm({ ...form, title: e.target.value }); setError(''); }}
          />

          {/* Listed under the business's own category - only shown so it can be moved. */}
          {categories.length > 1 || showCategory ? (
            showCategory ? (
              <SearchableSelect
                label="Listed under"
                hint="Which of your KAAMWALA categories this belongs to - it decides the questions below."
                placeholder="Search your categories"
                value={(categories.find((c) => c._id === form.category) || {}).name || ''}
                options={categories.map((c) => c.name)}
                onChange={(name) => {
                  const picked = categories.find((c) => c.name === name);
                  setForm({ ...form, category: picked ? picked._id : form.category });
                  setAttrs({});
                  setShowCategory(false);
                }}
                allowCreate={false}
              />
            ) : (
              <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 14 }}>
                <span className="t-tiny grow">
                  Listed under {(categories.find((c) => c._id === form.category) || {}).name || 'your business category'}
                </span>
                <button type="button" className="link-btn" onClick={() => setShowCategory(true)}>Change</button>
              </div>
            )
          ) : null}

          <SearchableSelect
            label={`Your own ${isProduct ? 'product' : 'service'} category`}
            hint="Your shop's own grouping - customers browse by it on your page. It stays inside your business."
            placeholder={food ? 'Starters, Main course, Breads…' : 'Winter wear, AC repairs…'}
            emptyText="None yet - type one to create your first."
            value={(shelfRows.find((r) => r.id === form.sectionId) || {}).label || form.section}
            options={sections}
            onChange={(label) => {
              const row = shelfRows.find((r) => r.label === label);
              setForm({ ...form, section: row ? row.label : label, sectionId: row ? row.id : null });
            }}
            onCreate={addSection}
          />

          {fieldDefs.length ? (
            <>
              <h3 className="h3" style={{ marginTop: 8 }}>What this category asks for</h3>
              <CategoryFields
                fields={fieldDefs}
                value={attrs}
                toggles={attrToggles}
                onChange={setAttrs}
                onToggle={(key, on) => setAttrToggles((t) => ({ ...t, [key]: on }))}
              />
            </>
          ) : null}

          <div className="form-row">
            <Input
              label="Selling price (₹)"
              icon="💰"
              className="grow"
              inputMode="numeric"
              value={form.price}
              onChange={(e) => { setForm({ ...form, price: e.target.value.replace(/\D/g, '') }); setError(''); }}
            />
            <Input
              label="MRP (optional)"
              className="grow"
              inputMode="numeric"
              value={form.mrp}
              onChange={(e) => setForm({ ...form, mrp: e.target.value.replace(/\D/g, '') })}
            />
          </div>

          {food ? (
            <>
              <label className="field-label">Veg or non-veg</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                {[['veg', '🟢 Veg'], ['non_veg', '🔴 Non-veg'], ['egg', '🟠 Contains egg']].map(([v, l]) => (
                  <Chip key={v} label={l} active={form.foodType === v} onClick={() => setForm({ ...form, foodType: v })} />
                ))}
              </div>
              <label className="field-label">Sizes / portions</label>
              <div className="t-tiny" style={{ marginBottom: 6 }}>Half, Full, 6 pcs — each with its own price. Leave empty for one price.</div>
              {form.variants.map((v, i) => (
                <div className="row" key={`var-${i}`} style={{ gap: 8, alignItems: 'center' }}>
                  <Input className="grow" placeholder="Half" value={v.label}
                    onChange={(e) => setForm({ ...form, variants: form.variants.map((x, y) => (y === i ? { ...x, label: e.target.value } : x)) })} />
                  <Input placeholder="₹" inputMode="numeric" value={v.price}
                    onChange={(e) => setForm({ ...form, variants: form.variants.map((x, y) => (y === i ? { ...x, price: e.target.value.replace(/\D/g, '') } : x)) })} />
                  <button type="button" className="link-btn" onClick={() => setForm({ ...form, variants: form.variants.filter((_, y) => y !== i) })}>✕</button>
                </div>
              ))}
              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                <Chip label="+ Half / Full" onClick={() => setForm({ ...form, variants: [...form.variants, { label: 'Half', price: '' }, { label: 'Full', price: '' }] })} />
                <Chip label="+ Per piece" onClick={() => setForm({ ...form, variants: [...form.variants, { label: '1 pc', price: '' }] })} />
                <Chip label="+ Other size" onClick={() => setForm({ ...form, variants: [...form.variants, { label: '', price: '' }] })} />
              </div>

              <label className="field-label">Add-ons & choices</label>
              <div className="t-tiny" style={{ marginBottom: 6 }}>Extra cheese, toppings, spice level. Each choice can carry a price.</div>
              {form.addonGroups.map((g, gi) => (
                <div className="card" key={`grp-${gi}`} style={{ background: 'var(--bg)', marginBottom: 10 }}>
                  <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                    <Input className="grow" placeholder="Group name, e.g. Toppings" value={g.title}
                      onChange={(e) => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, title: e.target.value } : x)) })} />
                    <button type="button" className="link-btn" onClick={() => setForm({ ...form, addonGroups: form.addonGroups.filter((_, y) => y !== gi) })}>✕</button>
                  </div>
                  <div className="row wrap" style={{ gap: 8, marginBottom: 8 }}>
                    <Chip label={g.required ? 'Customer must choose' : 'Optional'} active={g.required}
                      onClick={() => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, required: !x.required } : x)) })} />
                    <Chip label={g.multi ? 'Pick many' : 'Pick one'} active={!g.multi}
                      onClick={() => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, multi: !x.multi } : x)) })} />
                  </div>
                  {g.options.map((o, oi) => (
                    <div className="row" key={`opt-${gi}-${oi}`} style={{ gap: 8, alignItems: 'center' }}>
                      <Input className="grow" placeholder="Extra cheese" value={o.name}
                        onChange={(e) => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, options: x.options.map((z, k) => (k === oi ? { ...z, name: e.target.value } : z)) } : x)) })} />
                      <Input placeholder="₹ (0 = free)" inputMode="numeric" value={o.price}
                        onChange={(e) => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, options: x.options.map((z, k) => (k === oi ? { ...z, price: e.target.value.replace(/\D/g, '') } : z)) } : x)) })} />
                      <button type="button" className="link-btn" onClick={() => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, options: x.options.filter((_, k) => k !== oi) } : x)) })}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="link-btn" onClick={() => setForm({ ...form, addonGroups: form.addonGroups.map((x, y) => (y === gi ? { ...x, options: [...x.options, { name: '', price: '' }] } : x)) })}>
                    + Add a choice
                  </button>
                </div>
              ))}
              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                <Chip label="+ Add-ons (cheese, butter…)" onClick={() => setForm({ ...form, addonGroups: [...form.addonGroups, { title: 'Add-ons', required: false, multi: true, options: [{ name: 'Extra cheese', price: '30' }] }] })} />
                <Chip label="+ Spice level" onClick={() => setForm({ ...form, addonGroups: [...form.addonGroups, { title: 'Spice level', required: true, multi: false, options: [{ name: 'Mild', price: '0' }, { name: 'Medium', price: '0' }, { name: 'Spicy', price: '0' }] }] })} />
                <Chip label="+ Custom group" onClick={() => setForm({ ...form, addonGroups: [...form.addonGroups, { title: '', required: false, multi: true, options: [{ name: '', price: '' }] }] })} />
              </div>

              <label className="field-label">Served as</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                {['1 plate', '1 pc', '1 bowl', '1 glass', '1 cup', '1 kg', '500 g'].map((u) => (
                  <Chip key={u} label={u} active={form.unit === u} onClick={() => setForm({ ...form, unit: u })} />
                ))}
              </div>
              <Input
                label="How many can you serve today?"
                hint="Set 0 to mark the dish sold out."
                inputMode="numeric"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value.replace(/\D/g, '') })}
              />
            </>
          ) : isProduct ? (
            <>
              <label className="field-label">Unit</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
                {unitChoices.map((u) => (
                  <Chip key={u} label={u} active={form.unit === u} onClick={() => setForm({ ...form, unit: u })} />
                ))}
              </div>
              <div className="form-row">
                <Input
                  label="Stock quantity"
                  className="grow"
                  inputMode="numeric"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value.replace(/\D/g, '') })}
                />
                <Input
                  label="Brand (optional)"
                  className="grow"
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                />
              </div>

              <div className="split-2" style={{ gap: 12 }}>
                <Input
                  label="Model number"
                  placeholder="e.g. RS-1.5T-5S"
                  value={form.modelNumber}
                  onChange={(e) => setForm({ ...form, modelNumber: e.target.value })}
                />
                <Input
                  label="Warranty"
                  placeholder="e.g. 5 years on compressor"
                  value={form.warranty}
                  onChange={(e) => setForm({ ...form, warranty: e.target.value })}
                />
              </div>

              <label className="field-label">{customFields ? 'Custom fields' : 'Specifications'}</label>
              <div className="t-tiny" style={{ marginBottom: 6 }}>Anything a customer needs to know - tyre size, brand, warranty, colour. Shown on the product and to admin.</div>
              {form.specs.map((spec, i) => (
                <div className="row" key={`spec-${i}`} style={{ gap: 8, alignItems: 'center' }}>
                  <Input
                    className="grow"
                    placeholder="Capacity"
                    value={spec.key}
                    onChange={(e) => setForm({
                      ...form, specs: form.specs.map((s, x) => (x === i ? { ...s, key: e.target.value } : s)),
                    })}
                  />
                  <Input
                    className="grow"
                    placeholder="1.5 Ton"
                    value={spec.value}
                    onChange={(e) => setForm({
                      ...form, specs: form.specs.map((s, x) => (x === i ? { ...s, value: e.target.value } : s)),
                    })}
                  />
                  <button type="button" className="link-btn" onClick={() => setForm({ ...form, specs: form.specs.filter((_, x) => x !== i) })}>
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="link-btn" style={{ marginBottom: 10 }}
                onClick={() => setForm({ ...form, specs: [...form.specs, { key: '', value: '' }] })}>
                + Add a field
              </button>

              <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
                <Chip
                  label={form.installationAvailable ? '✓ Installation available' : 'Installation available'}
                  active={form.installationAvailable}
                  onClick={() => setForm({ ...form, installationAvailable: !form.installationAvailable })}
                />
                <Chip
                  label={form.deliveryAvailable ? '✓ Home delivery' : 'Home delivery'}
                  active={form.deliveryAvailable}
                  onClick={() => setForm({ ...form, deliveryAvailable: !form.deliveryAvailable })}
                />
              </div>
            </>
          ) : (
            <>
              <label className="field-label">Price unit</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
                {unitChoices.map((u) => (
                  <Chip
                    key={u}
                    label={u}
                    active={form.priceUnit === u}
                    onClick={() => setForm({ ...form, priceUnit: u })}
                  />
                ))}
              </div>
              <Input
                label="How long does it take? (minutes)"
                icon="⏱"
                inputMode="numeric"
                value={form.durationMinutes}
                onChange={(e) => setForm({ ...form, durationMinutes: e.target.value.replace(/\D/g, '') })}
              />

              <SpecFields
                label="Your own details"
                hint="Anything customers should know - warranty, how many people come, what it covers. Shown on the service and on your business page."
                keyPlaceholder="Warranty"
                valuePlaceholder="30 days"
                value={form.specs}
                onChange={(specs) => setForm({ ...form, specs })}
              />
            </>
          )}

          <Input
            label="Description (optional)"
            textarea
            rows={4}
            placeholder={isProduct ? 'Quality, packaging, origin…' : 'What is included in this service?'}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          {error ? <div className="field-error" style={{ marginBottom: 12 }}>{error}</div> : null}

          <Button
            title={editing ? `Save ${kind}` : `Add ${kind}`}
            type="submit"
            loading={saving}
            size="lg"
            full
          />
        </form>
      </Reveal>
    </div>
  );
};

export default AddCatalogueItemPage;
